import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem, FormControl,
    Table, TableHead, TableBody, TableRow, TableCell,
    Paper, CircularProgress, Alert, Stack, Chip, Autocomplete,
    Switch, FormControlLabel
} from '@mui/material';
import * as api from './variantGroupsApi';
import type { VariantGroup, VariantBrief, VariantDetail } from './variantGroupsApi';
import { sx13, paperSx } from '../common/detailView';


interface Props {
    variantGroupId: number | null;
}

export function VariantGroupView({ variantGroupId }: Props) {
    const { t } = useTranslation();

    const [variantGroup, setVariantGroup] = useState<VariantGroup | null>(null);
    const [variantGroupCopy, setVariantGroupCopy] = useState<VariantGroup | null>(null);
    const [variants, setVariants] = useState<VariantBrief[]>([]);
    const [availableDetails, setAvailableDetails] = useState<VariantDetail[]>([]);
    const [selectedVariant, setSelectedVariant] = useState<VariantBrief | null>(null);
    const [addedDetails, setAddedDetails] = useState<VariantDetail[]>([]);

    const [isNew, setIsNew] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isNewMode = variantGroupId == null;

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (isNewMode) {
                const variantBriefs = await api.getVariantBriefs();
                setVariants(variantBriefs);
                setVariantGroup({
                    id: 0,
                    groupName: '',
                    variant: null as unknown as VariantBrief,
                    variantDetails: [],
                });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getVariantGroup(variantGroupId as number);
                data.variantDetails = data.variantDetails ?? [];
                setVariantGroup(data);
                setIsNew(false);
                setIsEditing(false);

                const details = await api.getVariantDetails(data.variant.id);
                setAvailableDetails(details);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [variantGroupId, isNewMode]);

    useEffect(() => { loadData(); }, [loadData]);

    const loadDetailsForVariant = async (variantId: number) => {
        try {
            const details = await api.getVariantDetails(variantId);
            setAvailableDetails(details);
            setAddedDetails([]);
        } catch (err) {
            setError(String(err));
        }
    };

    const onVariantChange = (variant: VariantBrief | null) => {
        setSelectedVariant(variant);
        if (variant) {
            loadDetailsForVariant(variant.id);
        } else {
            setAvailableDetails([]);
        }
    };

    const enableEditing = () => {
        setVariantGroupCopy(JSON.parse(JSON.stringify(variantGroup)));
        setIsEditing(true);
    };

    const cancelEditing = () => {
        if (variantGroupCopy) setVariantGroup(variantGroupCopy);
        setIsEditing(false);
        setAddedDetails([]);
    };

    const updateGroupName = (name: string) => {
        setVariantGroup(prev => prev ? { ...prev, groupName: name } : prev);
    };

    const updateDetailField = (id: number, field: 'variantDetailName' | 'statisticName', value: string) => {
        setVariantGroup(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                variantDetails: prev.variantDetails.map(d =>
                    d.id === id ? { ...d, [field]: value } : d
                ),
            };
        });
    };

    const removeVariantDetail = (detail: VariantDetail) => {
        setVariantGroup(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                variantDetails: prev.variantDetails.filter(d => d !== detail && d.id !== detail.id),
            };
        });
    };

    const isAllValid = (): boolean => {
        if (!variantGroup || variantGroup.groupName.length === 0) return false;
        if (isNew && addedDetails.length === 0) return false;
        if (!isNew && variantGroup.variantDetails.length === 0) return false;
        return true;
    };

    const saveVariantGroup = async () => {
        if (!variantGroup || saving) return;
        setSaving(true);
        setError(null);
        try {
            const merged = mergeUnique(variantGroup.variantDetails, addedDetails);
            const detailIds = merged.map(d => d.id);

            let response: VariantGroup;
            if (isNew && selectedVariant) {
                response = await api.createVariantGroup(
                    variantGroup.groupName,
                    selectedVariant.id,
                    detailIds
                );
            } else {
                const detailUpdates = merged
                    .filter(d => d.id > 0)
                    .map(d => ({ id: d.id, statisticName: d.statisticName }));
                response = await api.updateVariantGroup(
                    variantGroup.id,
                    variantGroup.groupName,
                    detailIds,
                    detailUpdates
                );
            }

            setVariantGroup(response);
            setIsEditing(false);
            setIsNew(false);
            setAddedDetails([]);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }
    if (error && !variantGroup) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!variantGroup) return null;

    const sortedDetails = [...variantGroup.variantDetails].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    return (
        <Paper variant="outlined" sx={paperSx}>
            {/* Header */}
            <Box px={2} py={1} display="flex" alignItems="center" gap={1} bgcolor="grey.100">
                {!isEditing && (
                    <Button variant="outlined" color="inherit" size="medium" onClick={enableEditing}>
                        {t('common.edit')}
                    </Button>
                )}
                {isEditing && (
                    <>
                        <Button variant="contained" color="secondary" size="medium" onClick={saveVariantGroup} disabled={!isAllValid() || saving}>
                            {t('common.save')}
                        </Button>
                        {!isNew && (
                            <Button variant="outlined" color="inherit" size="medium" onClick={cancelEditing}>
                                {t('common.cancel')}
                            </Button>
                        )}
                    </>
                )}
                <Typography variant="h6" sx={{ fontSize: '14px', lineHeight: 1.43 }} fontWeight="bold">
                    {variantGroup.groupName}
                </Typography>
                {(
                    <FormControlLabel
                        sx={{ marginLeft: 'auto' }}
                        control={<Switch checked={true} onChange={() => { localStorage.setItem('devFeature_reactVariantGroupView', 'false'); window.location.reload(); }} size="small" sx={{
                            width: 34, height: 20, padding: 0, minWidth: 34,
                            '& .MuiSwitch-switchBase': { padding: '2px', '&.Mui-checked': { transform: 'translateX(14px)', color: '#fff', '& + .MuiSwitch-track': { backgroundColor: '#1976d2', opacity: 1 } } },
                            '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
                            '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#ccc', opacity: 1 }
                        }} />}
                        label={<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{t('common.tryNewFeatures')}</Typography>}
                    />
                )}
            </Box>

            <Box p={2}>
                <Stack spacing={3}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Group info table */}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={sx13}>{t('variantgroup.groupName')}</TableCell>
                                <TableCell sx={sx13}>{t('article.variant')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    {!isEditing ? (
                                        <Typography sx={sx13}>{variantGroup.groupName}</Typography>
                                    ) : (
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={variantGroup.groupName}
                                            onChange={e => updateGroupName(e.target.value)}
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography sx={sx13}>{variantGroup.variant?.variantName}</Typography>
                                    {isNew && (
                                        <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                                            <Select
                                                value={selectedVariant?.id ?? ''}
                                                onChange={e => {
                                                    const v = variants.find(vr => vr.id === e.target.value);
                                                    onVariantChange(v ?? null);
                                                }}
                                            >
                                                {variants.map(v => (
                                                    <MenuItem key={v.id} value={v.id}>{v.variantName}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    {/* Variant details table */}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={sx13}>{t('article.variant')}</TableCell>
                                <TableCell sx={sx13}>{t('variantdetail.statisticName')}</TableCell>
                                {isEditing && variantGroup.variantDetails.length > 1 && (
                                    <TableCell sx={sx13}>&nbsp;</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedDetails.map(d => (
                                <TableRow key={d.id}>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx13}>{d.variantDetailName}</Typography>
                                        ) : (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={d.variantDetailName}
                                                onChange={e => updateDetailField(d.id, 'variantDetailName', e.target.value)}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx13}>{d.statisticName}</Typography>
                                        ) : (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={d.statisticName ?? ''}
                                                onChange={e => updateDetailField(d.id, 'statisticName', e.target.value)}
                                            />
                                        )}
                                    </TableCell>
                                    {isEditing && variantGroup.variantDetails.length > 1 && (
                                        <TableCell>
                                            {d.id === -1 && (
                                                <Button size="small" variant="outlined" color="inherit" onClick={() => removeVariantDetail(d)}>
                                                    X
                                                </Button>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {isEditing && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography sx={{ ...sx13, fontWeight: 'bold', mb: 0.5 }}>{t('common.add')}:</Typography>
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={[...availableDetails].sort((a, b) => a.variantDetailName.localeCompare(b.variantDetailName))}
                                            getOptionLabel={o => o.variantDetailName}
                                            value={addedDetails}
                                            onChange={(_, value) => setAddedDetails(value)}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                            renderInput={params => <TextField {...params} placeholder={t('article.variant')} />}
                                            renderTags={(value, getTagProps) =>
                                                value.map((d, i) => <Chip {...getTagProps({ index: i })} key={d.id} label={d.variantDetailName} size="small" />)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Stack>
            </Box>

            {saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    );
}

function mergeUnique(existing: VariantDetail[], added: VariantDetail[]): VariantDetail[] {
    const merged = [...existing];
    for (const detail of added) {
        if (!merged.some(d => d.id === detail.id && d.variantDetailName === detail.variantDetailName && d.statisticName === detail.statisticName)) {
            merged.push(detail);
        }
    }
    return merged;
}
